'use client';

import { useState, useEffect, useCallback, useReducer } from 'react';
import {
  projects as dummyProjects,
  sourceRunStatuses as dummySourceStatuses,
  SIMULATE,
} from '@/lib/data-sanitised';
import type { SourceRunStatus } from '@/lib/sources/source-types';
import { ProjectRecord } from '@/lib/projects/project-types';

export type ProjectsLoadError = {
  kind: 'network' | 'unknown';
  summary: string;
};

interface State {
  projects: ProjectRecord[];
  sourceStatuses: SourceRunStatus[];
  lastUpdatedAt: string;
  isLoading: boolean;
  error: ProjectsLoadError | null;
}

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: { projects: ProjectRecord[]; sourceStatuses: SourceRunStatus[]; lastUpdatedAt: string } }
  | { type: 'FETCH_ERROR'; payload: { error: ProjectsLoadError; sourceStatuses: SourceRunStatus[] } };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };
    case 'FETCH_SUCCESS':
      return {
        projects: action.payload.projects,
        sourceStatuses: action.payload.sourceStatuses,
        lastUpdatedAt: action.payload.lastUpdatedAt,
        isLoading: false,
        error: null,
      };
    case 'FETCH_ERROR':
      return {
        ...state,
        projects: [],
        sourceStatuses: action.payload.sourceStatuses,
        isLoading: false,
        error: action.payload.error,
      };
    default:
      return state;
  }
}

interface UseProjectsDataReturn {
  projects: ProjectRecord[];
  sourceStatuses: SourceRunStatus[];
  lastUpdatedAt: string;
  isLoading: boolean;
  error: ProjectsLoadError | null;
  refetch: () => void;
}

export function useProjectsData(): UseProjectsDataReturn {
  const [state, dispatch] = useReducer(reducer, {
    projects: [],
    sourceStatuses: [],
    lastUpdatedAt: '',
    isLoading: true,
    error: null,
  });
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => {
    setFetchKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    dispatch({ type: 'FETCH_START' });

    const fetchData = async () => {
      await new Promise((resolve) => setTimeout(resolve, SIMULATE.loadingMs));
      if (cancelled) return;

      const now = new Date().toISOString();

      switch (SIMULATE.mode) {
        case 'ok': {
          const okStatuses = dummySourceStatuses.map((s) =>
            s.status === 'error' ? { ...s, status: 'ok' as const, message: undefined } : s
          );
          dispatch({
            type: 'FETCH_SUCCESS',
            payload: { projects: dummyProjects, sourceStatuses: okStatuses, lastUpdatedAt: now },
          });
          break;
        }
        case 'partialFailure': {
          dispatch({
            type: 'FETCH_SUCCESS',
            payload: { projects: dummyProjects, sourceStatuses: dummySourceStatuses, lastUpdatedAt: now },
          });
          break;
        }
        case 'empty': {
          const okStatuses = dummySourceStatuses.map((s) =>
            s.status === 'error' ? { ...s, status: 'ok' as const, message: undefined } : s
          );
          dispatch({
            type: 'FETCH_SUCCESS',
            payload: { projects: [], sourceStatuses: okStatuses, lastUpdatedAt: now },
          });
          break;
        }
        case 'hardError': {
          dispatch({
            type: 'FETCH_ERROR',
            payload: {
              error: { kind: 'network', summary: 'Failed to load projects' },
              sourceStatuses: dummySourceStatuses,
            },
          });
          break;
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [fetchKey]);

  return { ...state, refetch };
}
